import { Command, Option } from "clipanion";
import type { Readable, Writable } from "node:stream";
import {
  COMPANION_BRIDGE_LINE_MAX_LENGTH,
  parseRequestLine,
  serializeEvent,
  type CompanionBridgeEvent,
  type CompanionBridgeRequest,
} from "@m8t-stack/companion-bridge-contract";
import {
  chatCompanionMessage,
  converseCompanionMessage,
  decideCompanionMessage,
  historyCompanionMessages,
  recallCompanionMessages,
  rosterCompanions,
  sendCompanionMessage,
} from "../../lib/companion-chat-client.js";
import { checkCompanionUpdate } from "../../lib/companion-update-check.js";
import { runCompanionBridgeServe } from "../../lib/companion-bridge-serve.js";
import { defaultLocalCompanionInstallOptions } from "./install.js";

type EventSink = (event: CompanionBridgeEvent) => void;
type TerminalEvent = CompanionBridgeEvent;

export interface CompanionBridgeCommandDeps {
  readonly roster: (
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly update: () => Promise<TerminalEvent>;
  readonly send: (
    request: Extract<CompanionBridgeRequest, { type: "send" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly converse: (
    request: Extract<CompanionBridgeRequest, { type: "converse" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly chat: (
    request: Extract<CompanionBridgeRequest, { type: "chat" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly decide: (
    request: Extract<CompanionBridgeRequest, { type: "decide" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly history: (
    request: Extract<CompanionBridgeRequest, { type: "history" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
  readonly recall: (
    request: Extract<CompanionBridgeRequest, { type: "recall" }>,
    sink: EventSink,
  ) => Promise<TerminalEvent>;
}

const defaultDeps: CompanionBridgeCommandDeps = {
  roster: rosterCompanions,
  send: sendCompanionMessage,
  converse: converseCompanionMessage,
  chat: chatCompanionMessage,
  decide: decideCompanionMessage,
  history: historyCompanionMessages,
  recall: recallCompanionMessages,
  update: () => checkCompanionUpdate(defaultLocalCompanionInstallOptions()),
};

async function readSingleRequest(stdin: Readable): Promise<string> {
  let input = "";
  for await (const chunk of stdin) {
    input += String(chunk);
    if (input.length > COMPANION_BRIDGE_LINE_MAX_LENGTH + 2) {
      throw new Error("request exceeds bridge bound");
    }
  }

  if (input.endsWith("\r\n")) {
    input = input.slice(0, -2);
  } else if (input.endsWith("\n")) {
    input = input.slice(0, -1);
  }
  if (input.length === 0 || /[\r\n]/u.test(input)) {
    throw new Error("bridge accepts exactly one request");
  }
  return input;
}

function exitFor(terminal: TerminalEvent): number {
  if (terminal.type === "roster" || terminal.type === "completed") return 0;
  if (terminal.type === "history-end") return 0;
  // An answer of "nothing offered" is still an answer.
  if (terminal.type === "update") return 0;
  if (terminal.type === "indeterminate") return 4;
  if (terminal.type === "failed" && terminal.reason !== "bad-request") {
    return 3;
  }
  return 2;
}

export async function runCompanionBridge(
  stdin: Readable,
  stdout: Writable,
  stderr: Writable,
  deps: CompanionBridgeCommandDeps = defaultDeps,
): Promise<number> {
  let request: CompanionBridgeRequest;
  try {
    request = parseRequestLine(await readSingleRequest(stdin));
  } catch {
    stderr.write("bridge request rejected\n");
    return 2;
  }

  const sink: EventSink = (event) => {
    stdout.write(`${serializeEvent(event)}\n`);
  };

  try {
    let terminal: TerminalEvent;
    if (request.type === "roster") terminal = await deps.roster(sink);
    else if (request.type === "update") {
      terminal = await deps.update();
      sink(terminal);
    } else if (request.type === "history") {
      terminal = await deps.history(request, sink);
    } else if (request.type === "recall") {
      terminal = await deps.recall(request, sink);
    } else if (request.type === "converse") {
      terminal = await deps.converse(request, sink);
    } else if (request.type === "chat") {
      terminal = await deps.chat(request, sink);
    } else if (request.type === "decide") {
      terminal = await deps.decide(request, sink);
    } else terminal = await deps.send(request, sink);
    return exitFor(terminal);
  } catch {
    stderr.write("bridge request failed\n");
    return 3;
  }
}

export class CompanionBridgeCommand extends Command {
  static paths = [["companion", "_bridge"]];

  /**
   * One process serving many requests instead of one per request, so the
   * session keeps its authenticated context between them. A CLI predating the
   * flag rejects it outright, which is how the app knows to fall back.
   */
  serve = Option.Boolean("--serve", false);

  async execute(): Promise<number> {
    if (this.serve) {
      return runCompanionBridgeServe(
        this.context.stdin,
        this.context.stdout,
        this.context.stderr,
      );
    }
    return runCompanionBridge(
      this.context.stdin,
      this.context.stdout,
      this.context.stderr,
    );
  }
}
